import * as Cesium from 'cesium';

export const TREE_CYLINDER_HEIGHT = 0.1;
/**
 * Default height offset (geoid undulation) for tree cylinders.
 * This is a legacy fallback value. When zTranslation is available from the
 * point cloud's EPSG selection, it should be used instead for accurate
 * orthometric-to-ellipsoidal height conversion.
 */
export const TREE_HEIGHT_OFFSET = 42.113;
export const DEFAULT_TREE_COLOR = Cesium.Color.YELLOW.withAlpha(0.7);

export const orientationFromAxis = (position, axisVector = Cesium.Cartesian3.UNIT_Z) => {
  const axisMagnitude = Cesium.Cartesian3.magnitude(axisVector);
  if (!axisMagnitude) {
    return Cesium.Quaternion.IDENTITY;
  }

  const localDirection = Cesium.Cartesian3.multiplyByScalar(
    axisVector,
    1 / axisMagnitude,
    new Cesium.Cartesian3()
  );

  const enuToFixed = Cesium.Transforms.eastNorthUpToFixedFrame(position);
  const worldDirection = Cesium.Matrix4.multiplyByPointAsVector(
    enuToFixed,
    localDirection,
    new Cesium.Cartesian3()
  );

  if (!Cesium.Cartesian3.magnitude(worldDirection)) {
    return Cesium.Quaternion.IDENTITY;
  }

  const zAxis = Cesium.Cartesian3.normalize(worldDirection, new Cesium.Cartesian3());
  const upHint = Math.abs(zAxis.z) < 0.9 ? Cesium.Cartesian3.UNIT_Z : Cesium.Cartesian3.UNIT_X;
  const xAxis = Cesium.Cartesian3.normalize(
    Cesium.Cartesian3.cross(upHint, zAxis, new Cesium.Cartesian3()),
    new Cesium.Cartesian3()
  );

  if (!Cesium.Cartesian3.magnitude(xAxis)) {
    return Cesium.Quaternion.IDENTITY;
  }

  const yAxis = Cesium.Cartesian3.normalize(
    Cesium.Cartesian3.cross(zAxis, xAxis, new Cesium.Cartesian3()),
    new Cesium.Cartesian3()
  );

  const rotationMatrix = Cesium.Matrix3.fromColumnMajorArray([
    xAxis.x, xAxis.y, xAxis.z,
    yAxis.x, yAxis.y, yAxis.z,
    zAxis.x, zAxis.y, zAxis.z
  ]);
  return Cesium.Quaternion.fromRotationMatrix(rotationMatrix);
};

export const parseTreesFromGeoJson = (data) => {
  const isGeoJson = data?.type === 'FeatureCollection' && Array.isArray(data.features);
  if (!isGeoJson) {
    return [];
  }

  return data.features
    .map((feature, index) => {
      if (!feature?.geometry || feature.geometry.type !== 'Point') {
        return null;
      }

      const coords = feature.geometry.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) {
        return null;
      }

      const [lon, lat, elevation = 0] = coords;
      const props = feature.properties || {};
      const radius = parseFloat(props.radius);
      const dx = parseFloat(props.dx);
      const dy = parseFloat(props.dy);
      const dz = parseFloat(props.dz);
      const treeHeight = parseFloat(props.height);
      const treeIdRaw = props.id ?? feature.id ?? (index + 1);
      const numericTreeId = Number(treeIdRaw);
      const treeId = Number.isFinite(numericTreeId)
        ? numericTreeId
        : treeIdRaw;

      if (!Number.isFinite(radius)) {
        return null;
      }

      const hasOrientation = Number.isFinite(dx) && Number.isFinite(dy) && Number.isFinite(dz);

      return {
        id: treeId,
        lon,
        lat,
        elevation,
        x: feature.properties?.center_x,
        y: feature.properties?.center_y,
        radius,
        direction: hasOrientation ? new Cesium.Cartesian3(dx, dy, dz) : null,
        treeHeight: Number.isFinite(treeHeight) ? treeHeight : null,
        properties: props
      };
    })
    .filter(Boolean);
};

const resolveCesiumColor = (color) => {
  if (!color) {
    return DEFAULT_TREE_COLOR;
  }

  if (typeof color === 'string') {
    return Cesium.Color.fromCssColorString(color).withAlpha(DEFAULT_TREE_COLOR.alpha);
  }

  return color;
};

export const createTreeEntities = (viewer, trees, options = {}) => {
  if (!viewer || !Array.isArray(trees)) {
    return [];
  }

  const {
    color = DEFAULT_TREE_COLOR,
    cylinderHeight = TREE_CYLINDER_HEIGHT,
    heightOffset = TREE_HEIGHT_OFFSET
  } = options;
  const materialColor = resolveCesiumColor(color);

  return trees.map((tree) => {
    const basePosition = Cesium.Cartesian3.fromDegrees(
      tree.lon,
      tree.lat,
      (tree.elevation || 0) + heightOffset
    );
    const baseDirection = tree.direction || Cesium.Cartesian3.UNIT_Z;
    const orientation = orientationFromAxis(basePosition, baseDirection);

    const entity = viewer.entities.add({
      position: basePosition,
      orientation,
      cylinder: {
        length: cylinderHeight,
        topRadius: tree.radius,
        bottomRadius: tree.radius,
        material: materialColor
      }
    });

    entity.treeInfo = {
      id: tree.id,
      lon: tree.lon,
      lat: tree.lat,
      elevation: tree.elevation,
      radius: tree.radius,
      height: tree.treeHeight,
      axis: tree.direction
        ? { x: tree.direction.x, y: tree.direction.y, z: tree.direction.z }
        : null,
      entityId: entity.id,
      properties: { ...(tree.properties || {}) },
      basePosition: Cesium.Cartesian3.clone(basePosition),
      baseDirection: Cesium.Cartesian3.clone(baseDirection)
    };

    return entity;
  });
};

/**
 * Compute the world-space offset vector for a translation expressed in the
 * tileset's local ENU frame. Mirrors the translation portion of
 * `applyLocalTransform` so cylinders move with the point cloud.
 *
 *   offset = east * tx + north * ty + up * tz
 *
 * Rotation/scale are intentionally ignored — this is the translation-only path.
 */
export const computeEnuTranslationOffset = (geo, vals) => {
  if (!geo?.originalEnu || !vals) {
    return null;
  }
  const tx = Number.isFinite(vals.tx) ? vals.tx : 0;
  const ty = Number.isFinite(vals.ty) ? vals.ty : 0;
  const tz = Number.isFinite(vals.tz) ? vals.tz : 0;
  if (tx === 0 && ty === 0 && tz === 0) {
    return Cesium.Cartesian3.ZERO;
  }
  const east = Cesium.Matrix4.getColumn(geo.originalEnu, 0, new Cesium.Cartesian3());
  const north = Cesium.Matrix4.getColumn(geo.originalEnu, 1, new Cesium.Cartesian3());
  const up = Cesium.Matrix4.getColumn(geo.originalEnu, 2, new Cesium.Cartesian3());
  const offset = new Cesium.Cartesian3(0, 0, 0);
  Cesium.Cartesian3.add(offset, Cesium.Cartesian3.multiplyByScalar(east, tx, new Cesium.Cartesian3()), offset);
  Cesium.Cartesian3.add(offset, Cesium.Cartesian3.multiplyByScalar(north, ty, new Cesium.Cartesian3()), offset);
  Cesium.Cartesian3.add(offset, Cesium.Cartesian3.multiplyByScalar(up, tz, new Cesium.Cartesian3()), offset);
  return offset;
};

/**
 * Re-position tree cylinders so they follow the tileset's ENU translation.
 * Reads each entity's stored `basePosition` (set in createTreeEntities) and
 * writes position = basePosition + offset. Orientation is rebuilt from the
 * new position so the cylinder's up vector still matches local ENU-Up at
 * the new spot.
 */
export const applyTranslationToTreeEntities = (entities, geo, vals) => {
  if (!Array.isArray(entities) || !entities.length) {
    return;
  }
  const offset = computeEnuTranslationOffset(geo, vals);
  if (!offset) {
    return;
  }
  entities.forEach((entity) => {
    const info = entity?.treeInfo;
    if (!info?.basePosition) return;
    const newPosition = Cesium.Cartesian3.add(
      info.basePosition,
      offset,
      new Cesium.Cartesian3()
    );
    entity.position = newPosition;
    entity.orientation = orientationFromAxis(
      newPosition,
      info.baseDirection || Cesium.Cartesian3.UNIT_Z
    );
  });
};

export const buildTreeGeoJsonFromEntities = (entities, viewer) => {
  if (!Array.isArray(entities) || !viewer) {
    return null;
  }

  const features = [];
  entities.forEach((entity) => {
    if (!entity || entity.isDestroyed?.()) return;
    if (viewer?.entities && typeof viewer.entities.contains === 'function' && !viewer.entities.contains(entity)) return;

    const info = entity.treeInfo || {};
    // Prefer the untransformed basePosition so a save→reload cycle doesn't
    // bake the current dataset translation into the exported GeoJSON.
    let position = info.basePosition;
    if (!position) {
      position = entity.position;
      if (position?.getValue) {
        position = position.getValue(viewer.clock?.currentTime);
      }
    }
    const carto = position ? Cesium.Cartographic.fromCartesian(position) : null;

    const lon = Number.isFinite(info.lon) ? info.lon : (carto ? Cesium.Math.toDegrees(carto.longitude) : null);
    const lat = Number.isFinite(info.lat) ? info.lat : (carto ? Cesium.Math.toDegrees(carto.latitude) : null);
    const elevation = info.elevation;
    const radius = Number.isFinite(info.radius) ? info.radius : (entity?.cylinder?.topRadius ?? null);
    const height = Number.isFinite(info.height) ? info.height : null;
    const axis = info.axis;
    const id = info.id;
    const baseProps = info.properties;

    if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(radius)) {
      return;
    }

    const properties = {
      ...baseProps,
      id,
      radius,
    };

    if (height !== null && height !== undefined) {
      properties.height = height;
    }
    if (axis && Number.isFinite(axis.x) && Number.isFinite(axis.y) && Number.isFinite(axis.z)) {
      properties.dx = axis.x;
      properties.dy = axis.y;
      properties.dz = axis.z;
    }
    if (Number.isFinite(lon)) properties.center_lon = lon;
    if (Number.isFinite(lat)) properties.center_lat = lat;
    if (Number.isFinite(elevation)) properties.center_z = elevation;

    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          lon,
          lat,
          Number.isFinite(elevation) ? elevation : 0
        ]
      },
      properties
    });
  });

  if (!features.length) return null;
  return { type: 'FeatureCollection', features };
};

export const downloadGeoJson = (geojson, filename) => {
  if (!geojson) {
    return false;
  }
  const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return true;
};
